import { CTACard } from '../shared-components/CTACard';
import { NumberedList } from '../shared-components/NumberedList';
import { AccordionStep, ProgressAccordionStepProps } from '../shared-components/AccordionStep';
import { SimplifiedUserRole, ProgressStepStatusEnum } from '@platform/client/schemas';
import { PlanUpdateModal } from '../modal-components/PlanUpdateModal';
import { useState } from 'react';
import { HubExternalLink } from '../shared-components/HubExternalLink';

export const Planning = ({
  status,
  userType,
  isOpen,
  projectId,
  planningGuideUrl,
  onToggle,
  isTeam,
}: ProgressAccordionStepProps & {
  planningGuideUrl?: string | null;
  projectId?: number | null;
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const listItems = getListItems(userType, planningGuideUrl, isTeam);
  const headerContent = getHeaderContent(userType, isTeam);
  return (
    <>
      <AccordionStep title='Planning' status={status} isOpen={isOpen} onToggle={onToggle}>
        <p>🥳 It's a match!</p>
        <p>{headerContent}</p>
        <p className='font-semibold'>Your Planning Steps</p>
        <NumberedList items={listItems} />
        <CTACard
          title='Ready to get started?'
          description={
            isTeam ?
              "Anyone on your team can provide an update. When someone marks the step as complete, your project moves to the next step and everyone's view will update. You can also update us if scheduling is delayed or if you need help."
              : `Provide an update and we'll move you to the next step. You can also update us if the scheduling is delayed or if you need more help.`
          }
          ctaButtonProps={{
            size: 'sm',
            children: 'Provide update',
            variant: 'primary',
            onPress: () => setModalIsOpen(true),
            isDisabled: status !== ProgressStepStatusEnum.active_in_progress,
          }}
        />
      </AccordionStep>
      {projectId && (
        <PlanUpdateModal
          isOpen={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
          userType={userType}
          projectId={projectId}
          isTeam={isTeam ?? false}
        />
      )}
    </>
  );
};

const getHeaderContent = (userType: SimplifiedUserRole, isTeam?: boolean) => {
  switch (userType) {
    case SimplifiedUserRole.PROFESSIONAL:
      return `Great projects start with clarity. In this step, refine the scope together by deciding what is realistic in your timeline and defining clear deliverables or outcomes so ${isTeam ? 'your team feels' : 'you both feel'} confident and ready to move forward.`;
    case SimplifiedUserRole.REPRESENTATIVE:
      return `Great projects start with clarity. In this step, refine the scope ${isTeam ? 'with your team' : 'together'} by deciding what is realistic within the professional's available timeline and defining clear deliverables so you both feel confident and ready to move forward.`;
    default:
      throw new Error('Invalid user type');
  }
};

const getListItems = (
  userType: SimplifiedUserRole,
  planningGuideUrl?: string | null,
  isTeam?: boolean,
) => {
  const listItems = [
    <p>
      Complete the&nbsp;
      <HubExternalLink url={planningGuideUrl}>Planning Guide</HubExternalLink>
      &nbsp;during your call.
    </p>,
    <p>After the call, provide an update to move to the next step.</p>,
  ];

  if (isTeam) {
    listItems.unshift(
      <p>
        Schedule a second call to refine the project scope. This is where you'll define what's
        realistic within your timeline and agree on clear deliverables or outcomes. Most projects
        can be scoped in a single 1 hour call, and you can always schedule more time if needed.
      </p>,
    );
    if (userType === SimplifiedUserRole.PROFESSIONAL) {
      listItems.unshift(
        <p>
          Coordinate with your teammates to schedule your first call with the organization over
          email. Use this time to introduce yourselves, learn more about the organization's mission,
          and understand the context of the project.
        </p>,
      );
    } else if (userType === SimplifiedUserRole.REPRESENTATIVE) {
      listItems.unshift(
        <p>
          Email your team of professionals to schedule your first call. Use this conversation to
          introduce your team, share your organization's mission, and provide background on the
          project.
        </p>,
      );
    }
  } else {
    if (userType === SimplifiedUserRole.PROFESSIONAL) {
      listItems.unshift(
        <p>
          Reach out to the organization by email to schedule your planning call. Most projects can
          be scoped in a single 1 hour call, and you can always schedule more time if needed.
        </p>,
      );
    } else if (userType === SimplifiedUserRole.REPRESENTATIVE) {
      listItems.unshift(
        <p>
          Email the professional to schedule your planning call. Come prepared to share context
          about your organization and the challenge you're solving. Gather relevant materials or
          invite key team members beforehand.
        </p>,
      );
    }
  }
  return listItems;
};
